import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from "../../config";
import {
  FileText,
  Users,
  Calendar,
  Clock,
  Search,
  Filter,
  Eye,
  CheckCircle,
  AlertCircle,
  Edit,
  X,
  ChevronLeft,
  ChevronRight,
  User,
  BookOpen,
  BarChart3,
  TrendingUp,
  PieChart,
  GraduationCap,
  Printer,
  Download,
  Loader
} from 'lucide-react';

const GRADE_LIST = ['BV1', 'BV2', 'BV3', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const AssessmentManagement = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data state
  const [stats, setStats] = useState({
    totalAssessments: 0,
    draftAssessments: 0,
    publishedAssessments: 0,
    archivedAssessments: 0,
    assessmentsThisWeek: 0,
    assessmentsThisMonth: 0,
    topTeachers: [],
    topGrades: []
  });
  const [assessments, setAssessments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [totalAssessments, setTotalAssessments] = useState(0);

  // Filter and pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    grade: '',
    teacher: '',
    startDate: '',
    endDate: ''
  });
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);

  // API base URL imported from config

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchAssessments();
  }, [currentPage, filters]);

  // API helper function
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  };
  // Fetch initial data
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Fetch assessment statistics from assessment admin endpoints
      const statsData = await apiCall('/teacher/assessments/admin/stats');

      // Fetch teachers list
      const teachersData = await apiCall('/teacher/lesson-plans/admin/teachers/list');

      // Transform stats data to match our component structure
      const transformedStats = {
        totalAssessments: statsData.overview.total_assessments,
        draftAssessments: statsData.overview.draft_assessments,
        publishedAssessments: statsData.overview.published_assessments,
        archivedAssessments: statsData.overview.archived_assessments,
        assessmentsThisWeek: statsData.overview.assessments_this_week,
        assessmentsThisMonth: statsData.overview.assessments_this_month,
        topTeachers: statsData.top_creators.map(creator => ({
          name: creator.teacher_name,
          count: creator.assessment_count
        })),
        topGrades: statsData.distribution.grade_distribution.map(grade => ({
          name: grade._id,
          count: grade.count
        }))
      };

      setStats(transformedStats);
      setTeachers(teachersData);
    } catch (err) {
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch assessments with filters
  const fetchAssessments = async () => {
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString()
      });

      // Add filters if they exist
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status_filter', filters.status);
      if (filters.teacher) params.append('teacher_filter', filters.teacher);
      if (filters.grade) params.append('grade_filter', filters.grade);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);

      // Fetch assessments from assessment admin endpoints
      const response = await apiCall(`/teacher/assessments/admin/all?${params.toString()}`);

      setAssessments(response.assessments);
      setTotalAssessments(response.total);
    } catch (err) {
      setError(`Failed to fetch assessments: ${err.message}`);
    }
  };

  // View assessment details
  const viewAssessment = async (assessment) => {
    try {
      setLoading(true);
      const detailedAssessment = await apiCall(`/teacher/assessments/admin/${assessment.id}`);

      // Use the detailed assessment data directly
      setSelectedAssessment(detailedAssessment.assessment);
      setShowAssessmentModal(true);
    } catch (err) {
      setError(`Failed to load assessment details: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      grade: '',
      teacher: '',
      startDate: '',
      endDate: ''
    });
    setCurrentPage(1);
  };

  // Show notification
  const showNotification = (type, message) => {
    if (type === 'success') {
      setSuccess(message);
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(message);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Helper functions for assessment preview
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Group questions by type for display
  const groupQuestionsByType = (questions) => {
    if (!questions || !Array.isArray(questions)) return {};

    return questions.reduce((acc, question) => {
      if (!acc[question.type]) acc[question.type] = [];
      acc[question.type].push(question);
      return acc;
    }, {});
  };

  // Print assessment
  const printAssessment = () => {
    const previewContent = document.getElementById('assessment-preview-content')?.innerHTML;
    if (previewContent) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Assessment - ${selectedAssessment?.setup?.title || selectedAssessment?.title || 'Question Paper'}</title>
            <style>
              body { 
                font-family: 'Times New Roman', serif; 
                margin: 25px; 
                line-height: 1.7;
                color: #333;
                font-size: 14px;
              }
              .page-break-inside-avoid { page-break-inside: avoid; }
              @media print {
                body { margin: 20px; }
                .bg-gray-50 { background-color: #f9f9f9 !important; }
                .border { border: 1px solid #ccc !important; }
                .border-b { border-bottom: 1px solid #ccc !important; }
                .border-b-2 { border-bottom: 2px solid #666 !important; }
                .border-t-2 { border-top: 2px solid #666 !important; }
                .text-gray-600 { color: #666 !important; }
                .text-gray-700 { color: #444 !important; }
                .text-gray-800 { color: #333 !important; }
                .bg-gray-100 { background-color: #f0f0f0 !important; }
              }
              h3 { margin-top: 25px; margin-bottom: 15px; }
              .grid { display: grid; }
              .grid-cols-2 { grid-template-columns: 1fr 1fr; }
              .gap-6 { gap: 1.5rem; }
              .mb-3 { margin-bottom: 0.75rem; }
              .mb-4 { margin-bottom: 1rem; }
              .mb-6 { margin-bottom: 1.5rem; }
              .mb-8 { margin-bottom: 2rem; }
              .mb-10 { margin-bottom: 2.5rem; }
              .mt-4 { margin-top: 1rem; }
              .mt-6 { margin-top: 1.5rem; }
              .mt-16 { margin-top: 4rem; }
              .p-6 { padding: 1.5rem; }
              .pb-3 { padding-bottom: 0.75rem; }
              .pb-6 { padding-bottom: 1.5rem; }
              .pb-8 { padding-bottom: 2rem; }
              .pt-8 { padding-top: 2rem; }
              .ml-4 { margin-left: 1rem; }
              .ml-6 { margin-left: 1.5rem; }
              .ml-10 { margin-left: 2.5rem; }
              .mr-4 { margin-right: 1rem; }
              .text-center { text-align: center; }
              .text-right { text-align: right; }
              .text-sm { font-size: 0.875rem; }
              .text-base { font-size: 1rem; }
              .text-lg { font-size: 1.125rem; }
              .text-xl { font-size: 1.25rem; }
              .font-bold { font-weight: bold; }
              .font-semibold { font-weight: 600; }
              .leading-relaxed { line-height: 1.625; }
              .flex { display: flex; }
              .flex-1 { flex: 1; }
              .items-start { align-items: flex-start; }
              .justify-between { justify-content: space-between; }
              .space-y-2 > * + * { margin-top: 0.5rem; }
              .space-y-8 > * + * { margin-top: 2rem; }
              .rounded-lg { border-radius: 0.5rem; }
              .rounded-full { border-radius: 9999px; }
              .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
              .py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
            </style>
          </head>
          <body>
            ${previewContent}
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 500);
    }
  };

  // Download PDF (placeholder - would be implemented with jsPDF or similar)
  const downloadPDF = () => {
    console.log('PDF download functionality would be implemented with a PDF library');
    // This is a placeholder - actual implementation would use jsPDF or similar
    alert('PDF download functionality would be implemented with a PDF library like jsPDF');
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-yellow-100 text-yellow-800',
      published: 'bg-green-100 text-green-800',
      archived: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.draft;
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const icons = {
      draft: <Edit className="w-4 h-4" />,
      published: <CheckCircle className="w-4 h-4" />,
      archived: <FileText className="w-4 h-4" />
    };
    return icons[status] || icons.draft;
  };

  // Render stats cards
  const renderStats = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Assessments</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{stats.totalAssessments || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-blue-100">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">This Month</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats.assessmentsThisMonth || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-green-100">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Published</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">{stats.publishedAssessments || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-purple-100">
              <CheckCircle className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Draft</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{stats.draftAssessments || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-orange-100">
              <Edit className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render analytics section
  const renderAnalytics = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      {/* Top Teachers */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Top Assessment Creators</h3>
        </div>
        <div className="space-y-3">
          {stats.topTeachers?.slice(0, 5).map((teacher, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{teacher.name}</span>
              <span className="text-sm font-semibold text-gray-900">{teacher.count}</span>
            </div>
          ))}
          {stats.topTeachers?.length === 0 && (
            <p className="text-sm text-gray-500">No data available</p>
          )}
        </div>
      </div>

      {/* Top Grades */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-100">
            <BarChart3 className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Assessments by Grade</h3>
        </div>
        <div className="space-y-3">
          {stats.topGrades?.slice(0, 5).map((grade, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{grade.name}</span>
              <span className="text-sm font-semibold text-gray-900">{grade.count}</span>
            </div>
          ))}
          {stats.topGrades?.length === 0 && (
            <p className="text-sm text-gray-500">No data available</p>
          )}
        </div>
      </div>
    </div>
  );

  // Calculate pagination
  const totalPages = Math.ceil(totalAssessments / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalAssessments);

  if (loading) {
    return (
      <>
        <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
          <div className="w-full">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D67C2C] mx-auto mb-4"></div>
                <p className="text-gray-600">Loading assessment data...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

          {/* Notifications */}
          {error && (
            <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 bg-red-500 text-white" style={{ animation: 'slideInRight 0.5s ease-out' }}>
              <AlertCircle className="w-5 h-5" />
              <span className="font-[poppins]">{error}</span>
              <button onClick={() => setError(null)} className="ml-2 hover:opacity-80">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {success && (
            <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 bg-green-500 text-white" style={{ animation: 'slideInRight 0.5s ease-out' }}>
              <CheckCircle className="w-5 h-5" />
              <span className="font-[poppins]">{success}</span>
              <button onClick={() => setSuccess(null)} className="ml-2 hover:opacity-80">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Header */}
          <div className="pt-8 pb-8 text-center relative overflow-hidden">
            <div className="relative z-10">
              <div>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: '#D67C2C' }}>
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                  Assessment Management
                </h1>
                <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                  Monitor and analyze teacher assessments across your institution
                </p>
              </div>
            </div>
          </div>

          {/* Dashboard Stats */}
          {renderStats()}

          {/* Analytics Section */}
          {renderAnalytics()}

          {/* Filters Section */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                  <Filter className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Filter & Search</h2>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      placeholder="Search by title, teacher, grade..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Status</option>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Teacher</label>
                  <select
                    value={filters.teacher}
                    onChange={(e) => handleFilterChange('teacher', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Teachers</option>
                    {teachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
                  <select
                    value={filters.grade}
                    onChange={(e) => handleFilterChange('grade', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Grades</option>
                    {GRADE_LIST.map(g => <option key={g} value={g}>Grade {g}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Created From</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Created To</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  />
                </div>

                <div className="lg:col-span-2 flex items-end">
                  <button
                    onClick={clearFilters}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Assessments Table */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">All Assessments</h2>
                </div>
                <div className="text-sm text-gray-600">
                  Showing {startItem}-{endItem} of {totalAssessments} assessments
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title & Teacher</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade & Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assessments.map((assessment) => (
                    <tr key={assessment.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{assessment.title}</div>
                          <div className="text-sm text-gray-500 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {assessment.teacherName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{assessment.grade}</div>
                        <div className="text-sm text-gray-500">
                          {assessment.questionCount} questions • {assessment.totalMarks} marks • {assessment.duration}min
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(assessment.status)}`}>
                          {getStatusIcon(assessment.status)}
                          {assessment.status.charAt(0).toUpperCase() + assessment.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(assessment.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => viewAssessment(assessment)}
                          className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {assessments.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium text-gray-500">No assessments found</p>
                  <p className="text-sm text-gray-400">Try adjusting your filters or search criteria</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {startItem} to {endItem} of {totalAssessments} results
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                                ? 'bg-[#D67C2C] text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Assessment Detail Modal */}
        {showAssessmentModal && selectedAssessment && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden">
              {/* Modal Header */}
              <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-[#D67C2C] to-[#c06a25] text-white rounded-t-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Assessment Preview</h2>
                    <p className="text-white text-opacity-90 mt-1">
                      {selectedAssessment.setup?.title || selectedAssessment.title || 'Untitled Assessment'}
                    </p>
                    <p className="text-sm text-white text-opacity-75">
                      by {selectedAssessment.teacherInfo?.name || selectedAssessment.teacherName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => printAssessment()}
                    className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                    title="Print Assessment"
                  >
                    <Printer className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => downloadPDF()}
                    className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                    title="Download PDF"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowAssessmentModal(false)}
                    className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                    title="Close"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Assessment Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div id="assessment-preview-content" className="relative" style={{ fontFamily: 'Times New Roman, serif' }}>

                  {/* Assessment Metadata */}
                  <div className="mb-6 bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Subject/Domain</p>
                        <p className="text-sm font-medium flex items-center mt-1">
                          <BookOpen className="w-4 h-4 mr-2 text-blue-600" />
                          {selectedAssessment.setup?.domain || selectedAssessment.setup?.subject || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Grade</p>
                        <p className="text-sm font-medium flex items-center mt-1">
                          <GraduationCap className="w-4 h-4 mr-2 text-green-600" />
                          Grade {selectedAssessment.setup?.grade || selectedAssessment.grade || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Created</p>
                        <p className="text-sm font-medium flex items-center mt-1">
                          <Clock className="w-4 h-4 mr-2 text-gray-500" />
                          {formatDate(selectedAssessment.created_at || selectedAssessment.createdAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Status</p>
                        <p className="text-sm font-medium mt-1">
                          {selectedAssessment.status === 'published' ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Published
                            </span>
                          ) : selectedAssessment.status === 'draft' ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <Edit className="w-3 h-3 mr-1" />
                              Draft
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {selectedAssessment.status || 'Unknown'}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Teacher Information */}
                  <div className="mb-6 bg-blue-50 p-6 rounded-lg border border-blue-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600" />
                      Teacher Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm"><span className="font-medium">Name:</span> {selectedAssessment.teacherInfo?.name || selectedAssessment.teacherName}</p>
                        <p className="text-sm"><span className="font-medium">Email:</span> {selectedAssessment.teacherInfo?.email || selectedAssessment.teacherEmail}</p>
                      </div>
                      <div>
                        <p className="text-sm"><span className="font-medium">Created:</span> {formatDate(selectedAssessment.created_at || selectedAssessment.createdAt)}</p>
                        <p className="text-sm"><span className="font-medium">Last Updated:</span> {formatDate(selectedAssessment.updated_at || selectedAssessment.updatedAt)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Lesson Plan Information */}
                  {selectedAssessment.lessonPlanInfo && (
                    <div className="mb-6 p-6 bg-green-50 rounded-lg border border-green-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-green-600" />
                        Linked Lesson Plan
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm"><span className="font-medium">Title:</span> {selectedAssessment.lessonPlanInfo.title}</p>
                          <p className="text-sm"><span className="font-medium">Status:</span> {selectedAssessment.lessonPlanInfo.status}</p>
                        </div>
                        <div>
                          <p className="text-sm"><span className="font-medium">Domain:</span> {selectedAssessment.lessonPlanInfo.domain || 'N/A'}</p>
                          <p className="text-sm"><span className="font-medium">Grade:</span> {selectedAssessment.lessonPlanInfo.grade || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Assessment Header */}
                  <div className="text-center mb-10 border-b-2 border-gray-500 pb-8">
                    <div style={{ fontSize: '24px', lineHeight: '1.8', fontWeight: 'bold' }} className="text-gray-800">
                      {selectedAssessment.content?.header_text ? (
                        <div dangerouslySetInnerHTML={{ __html: selectedAssessment.content.header_text }}></div>
                      ) : (
                        <div>
                          <div className="text-2xl font-bold mb-2">Assessment Paper</div>
                          <div className="text-lg">{selectedAssessment.setup?.title || selectedAssessment.title || 'Sample Assessment'}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Assessment Details */}
                  <div className="grid grid-cols-2 gap-6 mb-8 text-base border-b-2 border-gray-300 pb-6">
                    <div><strong>Assessment Name:</strong> {selectedAssessment.setup?.title || selectedAssessment.title || 'Sample Assessment'}</div>
                    <div><strong>Duration:</strong> {selectedAssessment.setup?.duration || selectedAssessment.duration || 'Not specified'} minutes</div>
                    <div><strong>Total Questions:</strong> {selectedAssessment.questions?.length || selectedAssessment.questionCount || 0}</div>
                    <div><strong>Maximum Marks:</strong> {selectedAssessment.setup?.total_marks || selectedAssessment.totalMarks || (selectedAssessment.questions?.reduce((sum, q) => sum + (q.marks || 1), 0)) || 0}</div>
                  </div>

                  {/* Instructions */}
                  <div className="mb-10 bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <h3 className="font-bold mb-4 text-gray-800 text-lg">General Instructions:</h3>
                    <div className="text-base leading-relaxed">
                      {selectedAssessment.content?.instructions ? (
                        <div dangerouslySetInnerHTML={{ __html: selectedAssessment.content.instructions }}></div>
                      ) : (
                        <div>
                          <p>1. Read all questions carefully before answering.</p>
                          <p>2. Answer all questions in the space provided.</p>
                          <p>3. Write clearly and legibly.</p>
                          <p>4. Manage your time effectively.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Questions */}
                  {selectedAssessment.questions && selectedAssessment.questions.length > 0 ? (
                    <div className="space-y-8">
                      {/* Group questions by type */}
                      {Object.entries(
                        groupQuestionsByType(selectedAssessment.questions)
                      ).map(([type, typeQuestions], sectionIndex) => (
                        <div key={type} className="mb-10">
                          <h3 className="font-bold text-xl mb-6 text-gray-800 border-b-2 border-gray-400 pb-3">
                            Section {sectionIndex + 1}: {type.replace(/[-_]/g, ' ').toUpperCase()}
                            <span className="text-sm font-normal text-gray-600 ml-2">
                              ({typeQuestions.length} Question{typeQuestions.length > 1 ? 's' : ''})
                            </span>
                          </h3>

                          {typeQuestions.map((question, questionIndex) => {
                            // Find the global index for this question
                            const globalIndex = selectedAssessment.questions.findIndex(q => q.id === question.id) + 1;

                            return (
                              <div key={question.id} className="mb-8 page-break-inside-avoid">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start flex-1">
                                    <span className="font-bold mr-4 mt-1 text-lg">{globalIndex}.</span>
                                    <div className="flex-1">
                                      <div className="mb-3 leading-relaxed text-base">
                                        {question.question_text ? (
                                          <div dangerouslySetInnerHTML={{ __html: question.question_text }}></div>
                                        ) : (
                                          <span className="text-gray-500 italic">Question text not available</span>
                                        )}
                                      </div>

                                      {question.options && question.options.length > 0 && (
                                        <div className="mt-4 space-y-2">
                                          {question.options.map((opt, optIndex) => (
                                            <div key={optIndex} className="ml-10 text-base py-1">
                                              {String.fromCharCode(65 + optIndex)}) {opt.text}
                                              {opt.is_correct && selectedAssessment.status === 'draft' && (
                                                <span className="ml-2 text-green-600 font-medium">(Correct Answer)</span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {/* Answer space indicator for non-MCQ questions */}
                                      {(!question.options || question.options.length === 0) && (
                                        <div className="mt-6 mb-4">
                                          <div className="text-sm text-gray-600 mb-3 font-semibold">Answer:</div>
                                          <div className="border-b border-gray-400 mb-4" style={{ height: '24px' }}></div>
                                          <div className="border-b border-gray-400 mb-4" style={{ height: '24px' }}></div>
                                          {question.type === 'long_answer' && (
                                            <>
                                              <div className="border-b border-gray-400 mb-4" style={{ height: '24px' }}></div>
                                              <div className="border-b border-gray-400 mb-4" style={{ height: '24px' }}></div>
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="ml-6 text-right">
                                    <span className="text-sm font-bold text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
                                      [{question.marks || 1} Mark{(question.marks || 1) > 1 ? 's' : ''}]
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg">No questions available in this assessment</p>
                    </div>
                  )}

                  <div className="mt-16 pt-8 border-t-2 border-gray-500 text-center text-base text-gray-700">
                    <p className="font-bold">*** END OF QUESTION PAPER ***</p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50">
                <button
                  onClick={() => setShowAssessmentModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AssessmentManagement;